import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ESTAGIO_DO_CONVIDADO_LABELS,
  type ConviteCriadoDTO,
  type ConviteDTO,
  type VagasDeFundadorDTO,
} from '@adega/shared';
import { ErrorBox, Loading } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import {
  useAuth,
  useConvites,
  useCriarConvite,
  useDescartarPedido,
  usePedidosDeConvite,
  useRegenerarConvite,
  useRevogarConvite,
  useVagasDeFundador,
} from '../api/queries.js';
import type { PedidoDeConviteDTO } from '@adega/shared';
import { porExtenso } from '../lib/datas.js';

/**
 * Quem entra na adega.
 *
 * O cadastro é fechado: esta tela é a única porta. O admin registra o e-mail da
 * pessoa, o sistema devolve um código, e só o par (e-mail + código) cria conta.
 *
 * Cada convidado ganha a adega **dele**. Não é compartilhamento — ele não vê
 * nenhuma garrafa sua.
 */
export function Convites() {
  const { data: auth } = useAuth();
  const admin = auth?.user?.admin === true;

  const { data: convites, isLoading, error } = useConvites(admin);
  const { data: vagas } = useVagasDeFundador(admin);
  const criar = useCriarConvite();
  const [email, setEmail] = useState('');
  const [recemCriado, setRecemCriado] = useState<ConviteCriadoDTO | null>(null);

  // Quem não administra não deveria nem ter chegado aqui; a API responde 404 de
  // qualquer forma. Isto só evita a tela piscar um erro feio.
  if (auth && !admin) return <Navigate to="/" replace />;

  return (
    <div className="space-y-4">
      <PageHeader titulo="Convites" subtitulo="Quem pode criar conta no sistema" voltar="/perfil" />

      {vagas ? <VagasDeFundador vagas={vagas} /> : null}

      {/* Antes do formulário de propósito: quem já levantou a mão é mais
          urgente que lembrar de alguém para convidar. Quando não há pedido
          nenhum, nada aparece e a tela fica igual ao que sempre foi. */}
      <PedidosDeConvite
        admin={admin}
        ocupado={criar.isPending}
        aoConvidar={async (email) => {
          const criado = await criar.mutateAsync(email);
          setRecemCriado(criado);
        }}
      />

      <form
        className="card space-y-3"
        onSubmit={async (evento) => {
          evento.preventDefault();
          const criado = await criar.mutateAsync(email.trim());
          setRecemCriado(criado);
          setEmail('');
        }}
      >
        <div>
          <label htmlFor="email-convidado" className="text-sm font-semibold">
            Convidar alguém
          </label>
          <p className="mt-1 text-sm text-adega-muted">
            A pessoa vai criar a adega dela, do zero. Ela não enxerga as suas garrafas.
          </p>
        </div>

        <input
          id="email-convidado"
          type="email"
          required
          autoComplete="off"
          className="input w-full"
          placeholder="email@exemplo.com"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
        />

        <button type="submit" className="btn-primary w-full" disabled={criar.isPending}>
          {criar.isPending ? 'Gerando…' : 'Gerar convite'}
        </button>

        {/* No momento de emitir, e não só no resumo lá em cima: é aqui que se
            promete uma vaga que talvez não exista. Avisa, não bloqueia — quem
            entra depois das 50 entra do mesmo jeito, só sem o preço. */}
        {vagas?.semFolga ? (
          <p className="text-xs text-amber-200">
            {vagas.livres === 0
              ? 'As vagas de fundador acabaram: quem entrar agora não terá o preço de fundador.'
              : 'Os convites esperando já cobrem as vagas livres: este pode ficar sem o preço de fundador.'}
          </p>
        ) : null}

        {criar.error ? <ErrorBox erro={criar.error} /> : null}
      </form>

      {recemCriado ? (
        <CodigoNaTela criado={recemCriado} aoFechar={() => setRecemCriado(null)} />
      ) : null}

      {isLoading ? <Loading texto="Carregando convites…" /> : null}
      {error && !convites ? <ErrorBox erro={error} /> : null}

      {convites && convites.length > 0 ? (
        <section className="space-y-2">
          <h2 className="font-semibold">Convites emitidos ({convites.length})</h2>
          {convites.map((convite) => (
            <LinhaDoConvite
              key={convite.id}
              convite={convite}
              aoRegerar={(criado) => setRecemCriado(criado)}
            />
          ))}
        </section>
      ) : null}

      {convites && convites.length === 0 ? (
        <p className="text-sm text-adega-muted">Nenhum convite ainda. Só você tem conta aqui.</p>
      ) : null}
    </div>
  );
}

/**
 * A promessa do site, contada.
 *
 * O site diz "os 50 primeiros", e promessa pública vira compromisso: esta é a
 * conta que diz se ela ainda cabe. Ocupada é vaga de convite que virou conta —
 * inclusive de quem excluiu a conta depois, porque a vaga não reabre. Ver
 * `backend/src/domain/fundador.ts`.
 */
function VagasDeFundador({ vagas }: { vagas: VagasDeFundadorDTO }) {
  const porcento = Math.round((vagas.ocupadas / vagas.limite) * 100);

  return (
    <section className="card space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold">Vagas de fundador</h2>
        <p className="font-semibold text-adega-gold">
          {vagas.ocupadas} <span className="text-sm font-normal text-adega-muted">de {vagas.limite}</span>
        </p>
      </div>

      <div
        className="h-2 overflow-hidden rounded-full bg-adega-surface-2"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={vagas.limite}
        aria-valuenow={vagas.ocupadas}
        aria-label="Vagas de fundador ocupadas"
      >
        <div className="h-full rounded-full bg-adega-gold" style={{ width: `${porcento}%` }} />
      </div>

      <p className="text-xs text-adega-muted">
        {vagas.livres} {vagas.livres === 1 ? 'livre' : 'livres'} · {vagas.pendentes}{' '}
        {vagas.pendentes === 1 ? 'convite esperando' : 'convites esperando'}
      </p>

      {/* Acabou: o site continua prometendo, e a oferta escrita obriga. Quem
          lembra de trocar o texto é esta linha. */}
      {vagas.livres === 0 ? (
        <p className="text-xs text-amber-200">
          As {vagas.limite} vagas acabaram. Troque a oferta no site antes de convidar mais gente.
        </p>
      ) : null}
    </section>
  );
}

/**
 * Quem pediu convite pelo site de apresentação.
 *
 * Estes pedidos **não vêm do banco**: vêm de um KV na borda da Cloudflare, onde
 * uma Pages Function os grava. Ela funciona com o PC de casa desligado, que é o
 * ponto — a captura precisa de 24h, a leitura não, porque convite só se emite
 * com o app no ar de qualquer jeito.
 *
 * Consequência prática para esta tela: **a leitura pode falhar sozinha**. Se a
 * Cloudflare estiver fora, o resto da tela de convites continua inteiro, e esta
 * seção vira uma linha discreta. Nada de caixa vermelha para algo sobre o qual
 * o dono não tem o que fazer.
 */
function PedidosDeConvite({
  admin,
  ocupado,
  aoConvidar,
}: {
  admin: boolean;
  ocupado: boolean;
  aoConvidar: (email: string) => Promise<void>;
}) {
  const { data: pedidos, error } = usePedidosDeConvite(admin);

  if (error) {
    return (
      <p className="text-xs text-adega-muted">
        Não consegui ler os pedidos feitos pelo site agora.
      </p>
    );
  }

  if (!pedidos || pedidos.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="font-semibold text-adega-gold">
        Pediram convite pelo site ({pedidos.length})
      </h2>
      {pedidos.map((pedido) => (
        <LinhaDoPedido
          key={pedido.chave}
          pedido={pedido}
          ocupado={ocupado}
          aoConvidar={aoConvidar}
        />
      ))}
    </section>
  );
}

function LinhaDoPedido({
  pedido,
  ocupado,
  aoConvidar,
}: {
  pedido: PedidoDeConviteDTO;
  ocupado: boolean;
  aoConvidar: (email: string) => Promise<void>;
}) {
  const descartar = useDescartarPedido();
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className="card space-y-2 border-adega-gold/30">
      <div className="min-w-0">
        <p className="truncate font-medium">{pedido.nome || pedido.email}</p>
        {pedido.nome ? <p className="truncate text-sm text-adega-muted">{pedido.email}</p> : null}
        <p className="text-xs text-adega-muted">
          Pediu {pedido.em ? porExtenso(pedido.em) : 'em data desconhecida'}
          {pedido.pais && pedido.pais !== 'BR' ? ` · de ${pedido.pais}` : ''}
        </p>
      </div>

      {/* Já convidado e ainda na lista acontece quando a limpeza automática
          falhou — ela é deliberadamente silenciosa, porque o convite é que
          importa. Avisar aqui evita convidar a mesma pessoa duas vezes. */}
      {pedido.jaConvidado ? (
        <p className="text-xs text-amber-200">Já existe convite para este e-mail.</p>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className="btn-primary text-xs"
          disabled={ocupado}
          onClick={() => void aoConvidar(pedido.email)}
        >
          {pedido.jaConvidado ? 'Convidar de novo' : 'Convidar'}
        </button>

        {confirmando ? (
          <button
            type="button"
            className="btn-secondary text-xs text-red-200"
            disabled={descartar.isPending}
            onClick={() => void descartar.mutateAsync(pedido.chave)}
          >
            Confirmar
          </button>
        ) : (
          <button type="button" className="btn-ghost text-xs" onClick={() => setConfirmando(true)}>
            Descartar
          </button>
        )}
      </div>

      {descartar.error ? <ErrorBox erro={descartar.error} /> : null}
    </div>
  );
}

/**
 * O código, uma vez só.
 *
 * Ele é guardado em hash — mesma escolha do segredo de robot do Harbor. Como
 * não dá para consultar depois, a tela precisa tornar fácil mandar agora: o
 * recado já sai pronto para colar.
 */
function CodigoNaTela({ criado, aoFechar }: { criado: ConviteCriadoDTO; aoFechar: () => void }) {
  const [copiado, setCopiado] = useState<'codigo' | 'recado' | null>(null);

  const recado = [
    'Criei um convite para você usar o Minha Adega.',
    '',
    'E-mail: ' + criado.convite.email,
    'Código: ' + criado.codigo,
    '',
    'Vale até ' + porExtenso(criado.convite.expiresAt) + '.',
  ].join('\n');

  const copiar = async (texto: string, qual: 'codigo' | 'recado') => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(qual);
    } catch {
      // Sem permissão de área de transferência (acontece em http e em alguns
      // navegadores de celular): o texto continua na tela para copiar à mão.
      setCopiado(null);
    }
  };

  return (
    <div className="card space-y-3 border-adega-gold/40">
      <div>
        <p className="font-semibold text-adega-gold">Convite criado</p>
        <p className="mt-1 text-sm text-adega-muted">
          Este código aparece <strong>uma vez só</strong>. Mande agora para{' '}
          {criado.convite.email} — se perder, é só gerar outro.
        </p>
      </div>

      <p className="rounded-xl bg-adega-surface-2 px-4 py-3 text-center font-mono text-2xl tracking-[0.3em]">
        {criado.codigo}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => void copiar(criado.codigo, 'codigo')}
        >
          {copiado === 'codigo' ? 'Copiado!' : 'Copiar código'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => void copiar(recado, 'recado')}
        >
          {copiado === 'recado' ? 'Copiado!' : 'Copiar recado'}
        </button>
      </div>

      {/* Duas situações, uma consequência: se o e-mail não saiu — por não estar
          configurado ou por ter falhado —, quem manda o código é o dono. A tela
          não distingue os casos porque para quem está olhando dá no mesmo. */}
      {criado.enviadoPorEmail ? (
        <p className="text-xs text-emerald-200">
          Enviado por e-mail para {criado.convite.email}. Se não chegar em alguns minutos, peça
          para olhar o spam — ou mande o código você mesmo.
        </p>
      ) : (
        <p className="text-xs text-amber-200">
          <strong>O e-mail não saiu.</strong> Mande o código por onde preferir; o convite está
          válido do mesmo jeito.
        </p>
      )}

      <button type="button" className="btn-ghost w-full text-xs" onClick={aoFechar}>
        Já mandei, pode esconder
      </button>
    </div>
  );
}

const CORES: Record<ConviteDTO['estado'], string> = {
  pendente: 'bg-amber-500/20 text-amber-100',
  usado: 'bg-emerald-500/15 text-emerald-200',
  expirado: 'bg-adega-surface-2 text-adega-muted',
};

const TEXTOS: Record<ConviteDTO['estado'], string> = {
  pendente: 'Esperando',
  usado: 'Virou conta',
  expirado: 'Venceu',
};

function LinhaDoConvite({
  convite,
  aoRegerar,
}: {
  convite: ConviteDTO;
  aoRegerar: (criado: ConviteCriadoDTO) => void;
}) {
  const regerar = useRegenerarConvite();
  const revogar = useRevogarConvite();
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className="card space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium">{convite.email}</p>
          <p className="text-xs text-adega-muted">
            {convite.estado === 'usado'
              ? 'Entrou em ' + porExtenso(convite.usedAt)
              : 'Vale até ' + porExtenso(convite.expiresAt)}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className={`rounded-full px-2 py-0.5 text-xs ${CORES[convite.estado]}`}>
            {TEXTOS[convite.estado]}
          </span>
          {convite.fundador !== null ? (
            <span className="text-xs text-adega-gold">Fundador nº {convite.fundador}</span>
          ) : null}
        </div>
      </div>

      {/*
        Até onde a pessoa chegou. "Virou conta" sozinho não distinguia quem
        está usando de quem entrou uma vez e não voltou — e o primeiro
        convidado de verdade foi justamente o segundo caso: criou a adega com
        4 prateleiras e parou antes da primeira garrafa.

        O salto caro é de "adega criada" para "usando": configurar a adega é
        rápido e abstrato; cadastrar a primeira garrafa exige levantar, pegar
        a garrafa e fotografar o rótulo. É nesse degrau que se perde gente, e
        é por isso que ele aparece separado em vez de virar só "ativo".
      */}
      {convite.uso ? (
        <div className="flex items-center gap-2 border-t border-adega-surface-2 pt-2 text-xs">
          <span
            className={
              convite.uso.estagio === 'em_uso' ? 'text-adega-gold' : 'text-adega-muted'
            }
          >
            {ESTAGIO_DO_CONVIDADO_LABELS[convite.uso.estagio]}
          </span>
          {convite.uso.estagio !== 'sem_adega' ? (
            <span className="text-adega-muted">
              · {convite.uso.adegas} {convite.uso.adegas === 1 ? 'adega' : 'adegas'} ·{' '}
              {convite.uso.garrafas} {convite.uso.garrafas === 1 ? 'garrafa' : 'garrafas'}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Convite que virou conta não tem ação: apagá-lo deixaria a conta sem
          procedência, e é o único registro de como aquela pessoa entrou. */}
      {convite.estado !== 'usado' ? (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className="btn-secondary text-xs"
            disabled={regerar.isPending}
            onClick={async () => aoRegerar(await regerar.mutateAsync(convite.id))}
          >
            {convite.estado === 'expirado' ? 'Gerar de novo' : 'Novo código'}
          </button>

          {confirmando ? (
            <button
              type="button"
              className="btn-secondary text-xs text-red-200"
              disabled={revogar.isPending}
              onClick={() => void revogar.mutateAsync(convite.id)}
            >
              Confirmar
            </button>
          ) : (
            <button type="button" className="btn-ghost text-xs" onClick={() => setConfirmando(true)}>
              Cancelar convite
            </button>
          )}
        </div>
      ) : null}

      {regerar.error ? <ErrorBox erro={regerar.error} /> : null}
      {revogar.error ? <ErrorBox erro={revogar.error} /> : null}
    </div>
  );
}
